import dayjs, { Dayjs } from 'dayjs';
import useSymptomTrackingData, { CycleStats } from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LinearCalendarChart from './LinearCalendarChart';
import PeriodChart from './PeriodChart';
import SymptomsChart from './SymptomsChart';
import SymptomsChartLegend from './SymptomsChartLegend';

const PROGRESS_ITEM = 'flex flex-col gap-1.5';
const PROGRESS_LABEL = 'text-sm text-muted-foreground';
const PROGRESS_BAR_OUTER = 'h-2.5 w-full overflow-hidden rounded-full bg-background';
const PROGRESS_BAR_INNER = 'h-full rounded-full bg-inverse transition-[width] duration-200 ease-in-out';
const PROGRESS_TEXT = 'text-xs text-subtle-foreground';

const CompareCycles: React.FC = () => {
  const { t } = useTranslation();

  const {
    cycleDaysWithCenteredPeriod,
    getStatusValueForDisplayedDays,
    hasDataOtherThanPeriod,
    dataOtherThanPeriod,
    cycleHistory,
  } = useSymptomTrackingData();

  const notEnoughData = !hasDataOtherThanPeriod || cycleHistory?.length < 2;

  const graphsContainerRef = useRef<HTMLDivElement>(null);

  /*
  const maxLeftOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.min(max, days[0].diff(cycle.start, 'days'));
  }, 0);
  const maxRightOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.max(max, days[days.length - 1].diff(cycle.start, 'days'));
  }, 0);
  */
  const maxLeftOffset = -24;
  const maxRightOffset = 25;

  // scroll to the middle of the container
  useEffect(() => {
    if (graphsContainerRef.current) {
      if (!notEnoughData) {
        const { scrollWidth, clientWidth } = graphsContainerRef.current;
        graphsContainerRef.current.scrollTo({
          left: scrollWidth / 2 - clientWidth / 2,
        });
      } else {
        graphsContainerRef.current.scrollTo({
          left: 0,
        });
      }
    }
  }, [graphsContainerRef, notEnoughData]);

  const [selectedGraph, setSelectedGraph] = useState<'generalState' | 'period' | 'pain' | 'symptoms'>('period');
  const graphs: ('generalState' | 'period' | 'pain' | 'symptoms')[] = ['generalState', 'period', 'pain', 'symptoms'];

  // Progress calculations
  const requiredCycles = 2;
  const cyclesProgress = Math.min(
    100,
    Math.round((Math.min(cycleHistory?.length || 0, requiredCycles) / requiredCycles) * 100),
  );
  const hasAnySymptomDay = (dataOtherThanPeriod.daysWithData?.length || 0) > 0;
  const symptomsProgress = hasAnySymptomDay ? 100 : 0;

  const getGraphLabel = (graph: 'generalState' | 'period' | 'pain' | 'symptoms'): string => {
    switch (graph) {
      case 'generalState':
        return t('symptomTracking.compare.generalState');
      case 'period':
        return t('symptomTracking.compare.period');
      case 'pain':
        return t('symptomTracking.compare.pain');
      case 'symptoms':
        return t('symptomTracking.compare.symptoms');
      default:
        return '';
    }
  };

  const renderGraph = (days: Dayjs[], cycle: CycleStats) => {
    const dayRange: [Dayjs, Dayjs] = [cycle.start.add(maxLeftOffset, 'days'), cycle.start.add(maxRightOffset, 'days')];

    const displayedDays = cycle.predicted ? days.filter(day => day.isSameOrBefore(dayjs(), 'day')) : days;

    const topLabels: [Dayjs, string][] = [
      [
        cycle.start,
        t('symptomTracking.compare.cycleLabel', {
          date: cycle.start.toDate().toLocaleDateString(locale, { month: 'long', day: 'numeric' }),
        }),
      ],
    ];

    const key = days[0].date();
    switch (selectedGraph) {
      case 'generalState':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
            range={[0, 4]}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'period':
        return (
          <PeriodChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
            dayRange={dayRange}
            periodDots={
              cycle.predicted ? Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days')) : []
            } // empty list to maintain a consistent height, but do not display any dots for readability
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'pain':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('pain_level', days)}
            range={[0, 4]}
            reverseColors
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'symptoms':
        return (
          <SymptomsChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {!notEnoughData && (
        <div className="my-3 flex justify-between">
          {graphs.map(graph => (
            <a
              key={graph}
              onClick={() => {
                setSelectedGraph(graph);
                posthog.capture('tracking.compare_graph_selected', { graph });
              }}
              className={cn('cursor-pointer', selectedGraph === graph ? 'text-foreground' : 'text-muted-foreground')}
            >
              {getGraphLabel(graph)}
            </a>
          ))}
        </div>
      )}
      <div
        className={cn(
          'relative flex flex-col gap-4 overflow-y-hidden pt-4',
          notEnoughData ? 'overflow-x-hidden' : 'overflow-x-auto',
        )}
        ref={graphsContainerRef}
      >
        {notEnoughData ? (
          <div className="flex flex-col gap-3 p-3">
            <div className={PROGRESS_ITEM}>
              <div className={PROGRESS_LABEL}>{t('symptomTracking.compare.fillAtLeastTwoCycles')}</div>
              <div className={PROGRESS_BAR_OUTER}>
                <div className={PROGRESS_BAR_INNER} style={{ width: `${cyclesProgress}%` }} />
              </div>
              <div className={PROGRESS_TEXT}>
                {cycleHistory?.length}/{requiredCycles}
              </div>
            </div>
            <div className={PROGRESS_ITEM}>
              <div className={PROGRESS_LABEL}>{t('symptomTracking.compare.fillAtLeastOneSymptomDay')}</div>
              <div className={PROGRESS_BAR_OUTER}>
                <div className={PROGRESS_BAR_INNER} style={{ width: `${symptomsProgress}%` }} />
              </div>
              <div className={PROGRESS_TEXT}>{hasAnySymptomDay ? 1 : 0}/1</div>
            </div>
          </div>
        ) : (
          cycleDaysWithCenteredPeriod
            .reverse()
            .slice(0, 4)
            .filter(({ cycle }) => Boolean((cycle as any).start))
            .map(({ days, cycle }) => {
              return renderGraph(days, cycle as CycleStats);
            })
        )}
      </div>
      {selectedGraph === 'symptoms' && !notEnoughData && (
        <SymptomsChartLegend
          dayValues={getStatusValueForDisplayedDays(
            'symptoms',
            cycleDaysWithCenteredPeriod.flatMap(({ days }) => days),
            [],
          )}
        />
      )}
    </div>
  );
};

export default CompareCycles;
